import PropTypes from 'prop-types'

/**
 * ScheduleDescription (JavaScript/JSX)
 * -----------------------------------
 * Renders a human‑readable description like:
 *   "Will be sent every Tuesday at 3:39 PM"
 *
 * Props mirror your data shape. Optional `includeRange` adds a date window
 * (e.g., "from Aug 15, 2025 to Aug 20, 2025").
 */

function parseDate(input) {
  return input ? new Date(input) : undefined
}

function formatTime(date, locale) {
  return new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).format(date)
}

function formatDate(date, locale) {
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  }).format(date)
}

function toWeekday(date, locale) {
  return new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(date)
}

function joinDays(days) {
  if (!days || days.length === 0) return ''
  if (days.length === 1) return days[0]
  if (days.length === 2) return `${days[0]} and ${days[1]}`
  return `${days.slice(0, -1).join(', ')}, and ${days[days.length - 1]}`
}

export default function ScheduleDescription(props) {
  const {
    StartDate,
    EndDate,
    RepeatKind,
    DaysOfWeeks,
    locale = 'en-US',
    includeRange = false,
    tzLabel,
    prefix = 'Will be sent',
    className
  } = props

  const start = parseDate(StartDate)
  const end = parseDate(EndDate)

  if (!start || isNaN(start)) {
    return <span className={className}>Invalid start date</span>
  }

  const timeString = formatTime(start, locale)

  let cadence
  if (RepeatKind === 'Day') {
    cadence = 'every day'
  } else if (RepeatKind === 'Week') {
    let daysText =
      Array.isArray(DaysOfWeeks) && DaysOfWeeks.length > 0
        ? joinDays(DaysOfWeeks)
        : toWeekday(start, locale)
    cadence = `every ${daysText}`
  } else {
    // Fallback/unknown
    const startFull = new Intl.DateTimeFormat(locale, {
      dateStyle: 'medium',
      timeStyle: 'short'
    }).format(start)
    return <span className={className}>{`${prefix} starting ${startFull}`}</span>
  }

  const tz = tzLabel ? ` ${tzLabel}` : ''
  let description = `${prefix} ${cadence} at ${timeString}${tz}`

  if (includeRange && (start || end)) {
    const from = start ? formatDate(start, locale) : null
    const to = end && !isNaN(end) ? formatDate(end, locale) : null

    if (from && to) description += `, from ${from} to ${to}`
    else if (from) description += `, starting ${from}`
    else if (to) description += `, until ${to}`
  }

  return <span className={className}>{description}</span>
}

ScheduleDescription.propTypes = {
  StartDate: PropTypes.string.isRequired, // e.g. "2025-08-15 11:00 AM"
  EndDate: PropTypes.string,
  RepeatKind: PropTypes.oneOf(['Day', 'Week']).isRequired,
  DaysOfWeeks: PropTypes.arrayOf(PropTypes.string),
  locale: PropTypes.string,
  includeRange: PropTypes.bool,
  tzLabel: PropTypes.string,
  prefix: PropTypes.string,
  className: PropTypes.string
}

// --- Example usage ---
// <ScheduleDescription
//   StartDate="2025-08-15 11:00 AM"
//   EndDate="2025-08-20 11:00 AM"
//   RepeatKind="Week"
//   DaysOfWeeks={["Monday"]}
// />
